import React, { useMemo, useState } from 'react';
import { ENGINE_OPTIONS, canUseEngine, engineLabel, type Engine } from 'api/analysisApi';
import { useAuthStore } from 'store/useAuthStore';
import { useForkList, useForkOne } from 'hooks/useFork';
import type { BacktestRuleSet } from 'hooks/useBacktest';
import type { AnalysisAuditDetail } from 'types/history';
import { ForkComparison } from './ForkComparison';
import { fmtDateTime } from './format';
import './fork.css';

/** The engines a stored data set can actually be replayed through.
 *
 *  v0 / v0.1 are vision engines — they read a chart screenshot, not the
 *  structured payload a fork carries — so they are not replay targets. The
 *  `_nohm` entry is the same v1 engine with the heatmap withheld, which the
 *  backend takes as a flag rather than an engine name. */
const FORKABLE: Engine[] = ['brainer_v1', 'brainer_v1_nohm', 'enforcer', 'enforcer_v3', 'petar_v4', 'stojan_v5', 'claude'];

const FORK_ENGINE_OPTIONS = FORKABLE.map(
	(value) => ENGINE_OPTIONS.find((o) => o.value === value) ?? { value, label: value, badge: value },
);

interface ForkPanelProps {
	row: AnalysisAuditDetail;
	/** The rule set outcomes are read under — verdicts are stored per rule set, so
	 *  this must be the one the surrounding view is showing, never a default. */
	rules: BacktestRuleSet;
	/** Jump to another analysis (a fork of this one). */
	onOpenAnalysis?: (analysisId: string) => void;
}

/**
 * Replay this analysis's stored input through another engine.
 *
 * The framing matters more than the controls: what is being held constant is the
 * *input*, so any difference in the result is the engine rather than the market
 * having moved. That is the only comparison the audit log could not make before,
 * and it is why the panel leads with the data set rather than with the engine
 * picker.
 */
export function ForkPanel({ row, rules, onOpenAnalysis }: ForkPanelProps) {
	const list = useForkList(row.id, rules);
	const user = useAuthStore((s) => s.user);
	const engineOptions = FORK_ENGINE_OPTIONS.filter((o) => canUseEngine(user, o.value));
	const [engine, setEngine] = useState<Engine>(() => {
		// Default to something other than what produced this row — forking a row
		// onto its own engine is a repeatability check, not a comparison, so it
		// should be a deliberate choice rather than the default.
		const allowed = FORKABLE.filter((e) => canUseEngine(useAuthStore.getState().user, e));
		return allowed.find((e) => e !== row.engine) ?? 'enforcer';
	});
	const [repeats, setRepeats] = useState(1);
	const { running, result, error, fork, reset } = useForkOne();

	const blocked = row.fork_blocked_reason;
	const heatmap = engine !== 'brainer_v1_nohm';
	const apiEngine: Engine = heatmap ? engine : 'brainer_v1';

	/** The moment the replayed data describes — the parent's, for a fork. */
	const asOf = row.as_of ?? row.created_at;

	const parentHadHeatmap = useMemo(() => {
		const step1 = (row.input as { step1?: { heatmap_data?: unknown } } | null)?.step1;
		return Boolean(step1?.heatmap_data);
	}, [row.input]);

	const submit = () => {
		// Reload the comparison once the run lands, so the fork you just made joins
		// the table you were already looking at rather than only the result list.
		void fork(row.id, { engine: apiEngine, heatmap, repeats }).then((res) => {
			if (res) list.reload();
		});
	};

	return (
		<div className='fork-panel'>
			<div className='fork-intro'>
				<div className='fork-intro-title'>Replay this data set on another engine</div>
				<p className='fork-intro-body'>
					The market data below is re-sent to the engine you pick — nothing is fetched.
					Because the input is identical, any difference in the result is the engine, not
					the market moving. The fork is scored by the backtest against the candles that
					followed <strong>{fmtDateTime(asOf)}</strong>, the same ones this run was judged on.
				</p>
			</div>

			<dl className='fork-facts'>
				<div>
					<dt>Data set</dt>
					<dd>
						{row.asset} · {row.timeframe} · {row.exchange}
						{row.market ? ` · ${row.market}` : ''}
					</dd>
				</div>
				<div>
					<dt>As of</dt>
					<dd>{fmtDateTime(asOf)}</dd>
				</div>
				<div>
					<dt>Produced by</dt>
					<dd>{engineLabel(row.engine)}</dd>
				</div>
				<div>
					<dt>Heatmap stored</dt>
					<dd className={parentHadHeatmap ? '' : 'fork-fact-muted'}>
						{parentHadHeatmap ? 'yes' : 'none'}
					</dd>
				</div>
			</dl>

			{/* Existing forks first: on a parent that has already been forked, reviewing
			    is what you came for, and the create controls are the follow-up. */}
			{list.error && <div className='fork-error'>{list.error}</div>}
			{list.data && list.data.forks.length > 0 && (
				<ForkComparison
					parent={list.data.parent}
					forks={list.data.forks}
					rollups={list.rollups}
					rules={rules}
					onOpenAnalysis={onOpenAnalysis}
				/>
			)}

			{blocked ? (
				<div className='fork-blocked'>{blocked}</div>
			) : (
				<>
					<div className='fork-controls'>
						<label className='fork-field'>
							<span className='fork-field-label'>Engine</span>
							<select
								className='fork-select'
								value={engine}
								onChange={(e) => {
									setEngine(e.target.value as Engine);
									reset();
								}}
								disabled={running}
							>
								{engineOptions.map((o) => (
									<option key={o.value} value={o.value}>
										{o.label}
										{o.value === row.engine ? ' — same as this run' : ''}
									</option>
								))}
							</select>
						</label>

						<label className='fork-field fork-field-narrow'>
							<span className='fork-field-label'>Runs</span>
							<input
								className='fork-num'
								type='number'
								min={1}
								max={10}
								value={repeats}
								onChange={(e) =>
									setRepeats(Math.min(10, Math.max(1, Number(e.target.value) || 1)))
								}
								disabled={running}
							/>
						</label>

						<button className='fork-run-btn' onClick={submit} disabled={running}>
							{running ? 'Running…' : `Fork${repeats > 1 ? ` ×${repeats}` : ''}`}
						</button>
					</div>

					<div className='fork-notes'>
						{repeats === 1 && (
							<p className='fork-note'>
								One run per engine settles little on its own: v1 runs on Opus 5 with
								thinking on, which forces temperature 1, so its direction can differ
								run-to-run on this exact input. Raise <em>Runs</em>, or fork many
								analyses at once from the History toolbar, before reading a result as
								a verdict.
							</p>
						)}
						{!heatmap && (
							<p className='fork-note'>
								The heatmap is withheld — the control half of the with/without A/B.
							</p>
						)}
						{heatmap && !parentHadHeatmap && (
							<p className='fork-note fork-note-warn'>
								This run stored no heatmap, so the fork cannot be given one either —
								the cached heatmap is current, and injecting today's liquidity into a
								replay of this moment would be look-ahead. It will run without, and be
								recorded as <code>{apiEngine}_nohm</code>.
							</p>
						)}
						{running && (
							<p className='fork-note'>
								Running the full pipeline — a v1 fork is an Opus&nbsp;5 call and can
								take a few minutes. Leaving this tab does not cancel it.
							</p>
						)}
					</div>
				</>
			)}

			{error && <div className='fork-error'>{error}</div>}

			{result && (
				<div className='fork-results'>
					<div className='fork-results-title'>
						{result.forks.length} fork{result.forks.length === 1 ? '' : 's'} created
					</div>
					<ul className='fork-results-list'>
						{result.forks.map((f) => (
							<li key={f.audit_id} className='fork-result'>
								<span className={`fork-result-status fork-result-${f.status}`}>
									{f.status}
								</span>
								<span className='fork-result-engine'>{engineLabel(f.engine as Engine)}</span>
								{f.cost_usd != null && (
									<span className='fork-result-cost'>${f.cost_usd.toFixed(3)}</span>
								)}
								{f.error && <span className='fork-result-err'>{f.error}</span>}
								{f.status === 'success' && onOpenAnalysis && (
									<button
										className='fork-result-open'
										onClick={() => onOpenAnalysis(f.audit_id)}
									>
										Open
									</button>
								)}
							</li>
						))}
					</ul>
					<p className='fork-note'>
						Forks are hidden from History by default — switch the Forks filter to
						“include” or “only” to see them. They are scored by the next backtest sweep.
					</p>
				</div>
			)}
		</div>
	);
}
